import { useState, useMemo, Fragment } from 'react';
import {
  Container,
  Paper,
  Grid,
  TextField,
  Button,
  Typography,
  Autocomplete,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  LinearProgress,
  Box,
  Chip,
  Tooltip,
  IconButton,
  Collapse,
  Card,
  CardContent,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import HistoryIcon from '@mui/icons-material/History';
import MapIcon from '@mui/icons-material/Map';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import GpsOffIcon from '@mui/icons-material/GpsOff';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import BatteryFullIcon from '@mui/icons-material/BatteryFull';
import BatteryAlertIcon from '@mui/icons-material/BatteryAlert';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import SpeedIcon from '@mui/icons-material/Speed';
import PowerIcon from '@mui/icons-material/Power';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PageLayout from '../common/components/PageLayout';
import ManagementMenu from './ManagementMenu';
import PositionValue from '../common/components/PositionValue';
import { useTranslation } from '../common/components/LocalizationProvider';
import { useEffectAsync } from '../reactHelper';
import usePositionAttributes from '../common/attributes/usePositionAttributes';

const useStyles = makeStyles()((theme) => ({
  container: {
    marginTop: theme.spacing(2),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
  },
  filterPaper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  validIcon: {
    color: theme.palette.success.main,
  },
  invalidIcon: {
    color: theme.palette.error.main,
  },
  coordCell: {
    fontFamily: 'monospace',
    fontSize: '0.85rem',
  },
  expandButton: {
    padding: theme.spacing(0.5),
  },
  detailsRow: {
    backgroundColor: theme.palette.mode === 'dark'
      ? theme.palette.grey[800]
      : theme.palette.grey[50],
  },
  detailsContent: {
    padding: theme.spacing(2),
  },
  attributeCard: {
    height: '100%',
  },
  attributeGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: theme.spacing(1),
  },
  attributeItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.mode === 'dark'
      ? theme.palette.grey[700]
      : theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
  },
  attributeIcon: {
    color: theme.palette.primary.main,
    fontSize: '1.2rem',
  },
  attributeLabel: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  attributeValue: {
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  quickAttributes: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    alignItems: 'center',
  },
  quickAttrChip: {
    height: 24,
    '& .MuiChip-label': {
      padding: '0 8px',
      fontSize: '0.75rem',
    },
    '& .MuiChip-icon': {
      fontSize: '1rem',
      marginLeft: '4px',
    },
  },
}));

const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString();
};

// Helper to get icon for common attributes
const getAttributeIcon = (key, value) => {
  switch (key) {
    case 'batteryLevel':
      return value > 20 ? <BatteryFullIcon color="success" /> : <BatteryAlertIcon color="error" />;
    case 'battery':
    case 'power':
      return <PowerIcon color="primary" />;
    case 'fuel':
      return <LocalGasStationIcon color="warning" />;
    case 'ignition':
      return value ? <CheckCircleIcon color="success" /> : <WarningIcon color="disabled" />;
    case 'motion':
      return value ? <SpeedIcon color="primary" /> : <SpeedIcon color="disabled" />;
    case 'coolantTemp':
    case 'deviceTemp':
      return <ThermostatIcon color="info" />;
    case 'alarm':
      return <WarningIcon color="error" />;
    default:
      return null;
  }
};

// Priority attributes to show in quick view
const PRIORITY_ATTRIBUTES = [
  'ignition', 'motion', 'batteryLevel', 'battery', 'power', 'fuel',
  'odometer', 'totalDistance', 'alarm', 'blocked', 'sat', 'rssi',
];

const PositionsHistoryPage = () => {
  const { classes } = useStyles();
  const t = useTranslation();
  const positionAttributes = usePositionAttributes(t);

  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [fromDate, setFromDate] = useState(new Date(Date.now() - 24 * 60 * 60 * 1000));
  const [toDate, setToDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [positions, setPositions] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [expandedRows, setExpandedRows] = useState({});

  const toggleRowExpanded = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  useEffectAsync(async () => {
    const response = await fetch('/api/devices');
    if (response.ok) {
      setDevices(await response.json());
    }
  }, []);

  const handleSearch = async () => {
    if (!selectedDevice) return;

    setLoading(true);
    setPositions([]);

    try {
      const from = fromDate.toISOString();
      const to = toDate.toISOString();

      const response = await fetch(`/api/positions?deviceId=${selectedDevice.id}&from=${from}&to=${to}`);
      if (response.ok) {
        const data = await response.json();
        // Sort by fixTime descending
        data.sort((a, b) => new Date(b.fixTime) - new Date(a.fixTime));
        setPositions(data);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (positions.length === 0) return;

    const ExcelJS = (await import('exceljs')).default;
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Positions');

    // Get all unique attribute keys
    const attributeKeys = new Set();
    positions.forEach((pos) => {
      if (pos.attributes) {
        Object.keys(pos.attributes).forEach((key) => attributeKeys.add(key));
      }
    });

    const baseColumns = [
      { header: 'Fix Time', key: 'fixTime', width: 20 },
      { header: 'Server Time', key: 'serverTime', width: 20 },
      { header: 'Valid', key: 'valid', width: 8 },
      { header: 'Latitude', key: 'latitude', width: 15 },
      { header: 'Longitude', key: 'longitude', width: 15 },
      { header: 'Speed (km/h)', key: 'speed', width: 12 },
      { header: 'Course', key: 'course', width: 10 },
      { header: 'Address', key: 'address', width: 40 },
      { header: 'Protocol', key: 'protocol', width: 12 },
    ];

    const attributeColumns = Array.from(attributeKeys).sort().map((key) => ({
      header: key,
      key: `attr_${key}`,
      width: 15,
    }));

    worksheet.columns = [...baseColumns, ...attributeColumns];

    positions.forEach((pos) => {
      const row = {
        fixTime: formatDateTime(pos.fixTime),
        serverTime: formatDateTime(pos.serverTime),
        valid: pos.valid ? 'Yes' : 'No',
        latitude: pos.latitude?.toFixed(6),
        longitude: pos.longitude?.toFixed(6),
        speed: (pos.speed * 1.852).toFixed(1),
        course: pos.course?.toFixed(0),
        address: pos.address || '-',
        protocol: pos.protocol || '-',
      };

      // Add attributes
      attributeKeys.forEach((key) => {
        row[`attr_${key}`] = pos.attributes?.[key] ?? '';
      });

      worksheet.addRow(row);
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `positions_${selectedDevice?.name || 'device'}_${new Date().toISOString().split('T')[0]}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openInMaps = (lat, lng) => {
    window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
  };

  const stats = useMemo(() => {
    if (positions.length === 0) return null;
    const validCount = positions.filter((p) => p.valid).length;
    const avgSpeed = positions.reduce((acc, p) => acc + p.speed, 0) / positions.length;
    const maxSpeed = Math.max(...positions.map((p) => p.speed));
    return {
      total: positions.length,
      valid: validCount,
      invalid: positions.length - validCount,
      avgSpeed: (avgSpeed * 1.852).toFixed(1),
      maxSpeed: (maxSpeed * 1.852).toFixed(1),
    };
  }, [positions]);

  // Render quick attribute chips for the main row
  const renderQuickAttributes = (pos) => {
    if (!pos.attributes) return null;

    const attrs = pos.attributes;
    const chips = [];

    // Add ignition
    if (attrs.ignition !== undefined) {
      chips.push(
        <Chip
          key="ignition"
          icon={getAttributeIcon('ignition', attrs.ignition)}
          label={attrs.ignition ? t('positionIgnition') : 'Off'}
          size="small"
          color={attrs.ignition ? 'success' : 'default'}
          variant="outlined"
          className={classes.quickAttrChip}
        />,
      );
    }

    // Add battery level
    if (attrs.batteryLevel !== undefined) {
      chips.push(
        <Chip
          key="battery"
          icon={getAttributeIcon('batteryLevel', attrs.batteryLevel)}
          label={`${attrs.batteryLevel}%`}
          size="small"
          color={attrs.batteryLevel > 20 ? 'success' : 'error'}
          variant="outlined"
          className={classes.quickAttrChip}
        />,
      );
    }

    // Add fuel if available
    if (attrs.fuel !== undefined) {
      chips.push(
        <Chip
          key="fuel"
          icon={<LocalGasStationIcon />}
          label={`${attrs.fuel.toFixed?.(1) ?? attrs.fuel} L`}
          size="small"
          color="warning"
          variant="outlined"
          className={classes.quickAttrChip}
        />,
      );
    }

    // Add alarm if present
    if (attrs.alarm) {
      chips.push(
        <Chip
          key="alarm"
          icon={<WarningIcon />}
          label={attrs.alarm}
          size="small"
          color="error"
          variant="filled"
          className={classes.quickAttrChip}
        />,
      );
    }

    // Show attribute count
    const attrCount = Object.keys(attrs).length;
    if (attrCount > 0) {
      chips.push(
        <Chip
          key="count"
          label={`+${attrCount}`}
          size="small"
          variant="outlined"
          className={classes.quickAttrChip}
        />,
      );
    }

    return <Box className={classes.quickAttributes}>{chips}</Box>;
  };

  // Render detailed attributes in expanded row
  const renderExpandedAttributes = (pos) => {
    if (!pos.attributes) return null;

    const attrs = pos.attributes;
    const sortedKeys = Object.keys(attrs).sort((a, b) => {
      const aPriority = PRIORITY_ATTRIBUTES.indexOf(a);
      const bPriority = PRIORITY_ATTRIBUTES.indexOf(b);
      if (aPriority !== -1 && bPriority !== -1) return aPriority - bPriority;
      if (aPriority !== -1) return -1;
      if (bPriority !== -1) return 1;
      return a.localeCompare(b);
    });

    return (
      <Box className={classes.attributeGrid}>
        {sortedKeys.map((key) => {
          const attrDef = positionAttributes[key];
          const icon = getAttributeIcon(key, attrs[key]);

          return (
            <Box key={key} className={classes.attributeItem}>
              {icon && <Box className={classes.attributeIcon}>{icon}</Box>}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography className={classes.attributeLabel} noWrap>
                  {attrDef?.name || key}
                </Typography>
                <Typography className={classes.attributeValue} noWrap>
                  <PositionValue position={pos} attribute={key} />
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <PageLayout menu={<ManagementMenu />} breadcrumbs={['reportManagement', 'reportPositionsHistory']}>
      <Container maxWidth={false} className={classes.container}>
        {/* Filters */}
        <Paper className={classes.filterPaper}>
          <Typography variant="h6" gutterBottom>
            <HistoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {t('reportPositionsHistory')}
          </Typography>

          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <Autocomplete
                options={devices}
                getOptionLabel={(option) => option.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                value={selectedDevice}
                onChange={(e, value) => setSelectedDevice(value)}
                renderOption={(props, option) => {
                  const { key, ...rest } = props;
                  return (
                    <li {...rest} key={option.id}>
                      {option.name}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField {...params} label={t('deviceTitle')} size="small" />
                )}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={t('reportFrom')}
                  value={fromDate}
                  onChange={setFromDate}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={t('reportTo')}
                  value={toDate}
                  onChange={setToDate}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <Button
                variant="contained"
                fullWidth
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                disabled={loading || !selectedDevice}
              >
                {t('sharedSearch')}
              </Button>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<FileDownloadIcon />}
                onClick={handleExport}
                disabled={positions.length === 0}
              >
                Excel
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {/* Stats */}
        {stats && (
          <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip label={`Total: ${stats.total}`} color="primary" />
            <Chip label={`${t('positionValid')}: ${stats.valid}`} color="success" variant="outlined" />
            <Chip label={`${t('positionInvalid')}: ${stats.invalid}`} color="error" variant="outlined" />
            <Chip label={`${t('reportAverageSpeed')}: ${stats.avgSpeed} km/h`} />
            <Chip label={`${t('reportMaximumSpeed')}: ${stats.maxSpeed} km/h`} />
          </Box>
        )}

        {/* Results Table */}
        {positions.length > 0 && (
          <Paper>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40 }} />
                  <TableCell>{t('positionFixTime')}</TableCell>
                  <TableCell align="center">{t('positionValid')}</TableCell>
                  <TableCell>{t('positionLatitude')}</TableCell>
                  <TableCell>{t('positionLongitude')}</TableCell>
                  <TableCell align="right">{t('positionSpeed')} (km/h)</TableCell>
                  <TableCell align="right">{t('positionCourse')}</TableCell>
                  <TableCell>{t('positionAddress')}</TableCell>
                  <TableCell>{t('sharedAttributes')}</TableCell>
                  <TableCell align="center">{t('sharedActions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {positions
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((pos) => (
                    <Fragment key={pos.id}>
                      <TableRow
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => toggleRowExpanded(pos.id)}
                      >
                        <TableCell className={classes.expandButton}>
                          <IconButton size="small">
                            {expandedRows[pos.id] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>{formatDateTime(pos.fixTime)}</TableCell>
                        <TableCell align="center">
                          {pos.valid ? (
                            <GpsFixedIcon className={classes.validIcon} fontSize="small" />
                          ) : (
                            <GpsOffIcon className={classes.invalidIcon} fontSize="small" />
                          )}
                        </TableCell>
                        <TableCell className={classes.coordCell}>
                          {pos.latitude?.toFixed(6)}
                        </TableCell>
                        <TableCell className={classes.coordCell}>
                          {pos.longitude?.toFixed(6)}
                        </TableCell>
                        <TableCell align="right">
                          {(pos.speed * 1.852).toFixed(1)}
                        </TableCell>
                        <TableCell align="right">{pos.course?.toFixed(0)}°</TableCell>
                        <TableCell sx={{ maxWidth: 200 }}>
                          <Typography variant="body2" noWrap title={pos.address}>
                            {pos.address || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 350 }}>
                          {renderQuickAttributes(pos)}
                        </TableCell>
                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                          <Tooltip title={t('linkGoogleMaps')}>
                            <IconButton
                              size="small"
                              onClick={() => openInMaps(pos.latitude, pos.longitude)}
                            >
                              <MapIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                      {/* Expandable Details Row */}
                      <TableRow className={classes.detailsRow}>
                        <TableCell colSpan={10} sx={{ p: 0 }}>
                          <Collapse in={expandedRows[pos.id]} timeout="auto" unmountOnExit>
                            <Box className={classes.detailsContent}>
                              <Typography variant="subtitle2" gutterBottom>
                                {t('sharedAttributes')} ({Object.keys(pos.attributes || {}).length})
                              </Typography>
                              {renderExpandedAttributes(pos)}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={positions.length}
              page={page}
              onPageChange={(e, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[25, 50, 100, 500]}
            />
          </Paper>
        )}

        {!loading && positions.length === 0 && selectedDevice && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <HistoryIcon sx={{ fontSize: 48, opacity: 0.5, mb: 2 }} />
            <Typography color="textSecondary">{t('sharedNoData')}</Typography>
          </Paper>
        )}
      </Container>
    </PageLayout>
  );
};

export default PositionsHistoryPage;
